/**
 * AdminFlexibleCompensationConfigV2 - Flexible compensation config with Tailwind/shadcn UI.
 * Same logic as AdminFlexibleCompensationConfig, new design.
 */
import React, { useContext, useEffect, useState } from 'react';
import {
	Loader2,
	Check,
	Heart,
	Car,
	Utensils,
	Baby,
	GraduationCap,
	CreditCard
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { ArcaContext } from '../../context/ArcaContext.context';
import getFlexibleCompensationConfig from '../../api/companies/getFlexibleCompensationConfig';
import updateFlexibleCompensationConfig from '../../api/companies/updateFlexibleCompensationConfig';
import {
	FlexibleCompensationConfigI,
	FlexibleCompensationConfigUpdateI
} from '../../api/companies/interfaces/FlexibleCompensationConfig';

interface FlexCompItem {
	key: keyof FlexibleCompensationConfigUpdateI;
	amountKey: keyof FlexibleCompensationConfigUpdateI;
	labelKey: string;
	icon: React.ReactNode;
	placeholder: string;
	isAnnual?: boolean;
}

const FLEX_COMP_ITEMS: FlexCompItem[] = [
	{
		key: 'hasCompanyHealthInsurance',
		amountKey: 'companyHealthInsuranceAmount',
		labelKey: 'admin.flexibleCompensation.healthInsurance',
		icon: <Heart className="w-5 h-5" />,
		placeholder: '50'
	},
	{
		key: 'transportAvailable',
		amountKey: 'transportAmount',
		labelKey: 'admin.flexibleCompensation.transport',
		icon: <Car className="w-5 h-5" />,
		placeholder: '100'
	},
	{
		key: 'foodAvailable',
		amountKey: 'foodAmount',
		labelKey: 'admin.flexibleCompensation.food',
		icon: <Utensils className="w-5 h-5" />,
		placeholder: '150'
	},
	{
		key: 'childcareAvailable',
		amountKey: 'childcareAmount',
		labelKey: 'admin.flexibleCompensation.childcare',
		icon: <Baby className="w-5 h-5" />,
		placeholder: '200'
	},
	{
		key: 'trainingAvailable',
		amountKey: 'trainingAmount',
		labelKey: 'admin.flexibleCompensation.training',
		icon: <GraduationCap className="w-5 h-5" />,
		placeholder: '500',
		isAnnual: true
	}
];

const AdminFlexibleCompensationConfigV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token } = useContext(ArcaContext);

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const [config, setConfig] = useState<FlexibleCompensationConfigUpdateI>({
		hasCompanyHealthInsurance: false,
		companyHealthInsuranceAmount: undefined,
		transportAvailable: false,
		transportAmount: undefined,
		foodAvailable: false,
		foodAmount: undefined,
		childcareAvailable: false,
		childcareAmount: undefined,
		trainingAvailable: false,
		trainingAmount: undefined
	});

	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const response = await getFlexibleCompensationConfig(token);
				if (response.success && response.data) {
					const data: FlexibleCompensationConfigI = response.data;
					setConfig({
						hasCompanyHealthInsurance: data.hasCompanyHealthInsurance,
						companyHealthInsuranceAmount: data.companyHealthInsuranceAmount,
						transportAvailable: data.transportAvailable,
						transportAmount: data.transportAmount,
						foodAvailable: data.foodAvailable,
						foodAmount: data.foodAmount,
						childcareAvailable: data.childcareAvailable,
						childcareAmount: data.childcareAmount,
						trainingAvailable: data.trainingAvailable,
						trainingAmount: data.trainingAmount
					});
				}
			} catch (error) {
				console.error('Error fetching flexible compensation config:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchConfig();
	}, [token]);

	const handleSave = async () => {
		setSaving(true);
		setSaved(false);
		try {
			const response = await updateFlexibleCompensationConfig(token, config);
			if (response.success) {
				setSaved(true);
				setTimeout(() => setSaved(false), 3000);
			}
		} catch (error) {
			console.error('Error saving flexible compensation config:', error);
		} finally {
			setSaving(false);
		}
	};

	const toggleItem = (
		key: keyof FlexibleCompensationConfigUpdateI,
		amountKey: keyof FlexibleCompensationConfigUpdateI
	) => {
		setConfig((prev) => ({
			...prev,
			[key]: !prev[key],
			[amountKey]: !prev[key] ? prev[amountKey] : undefined
		}));
	};

	const updateAmount = (amountKey: keyof FlexibleCompensationConfigUpdateI, value: string) => {
		setConfig((prev) => ({
			...prev,
			[amountKey]: value ? parseFloat(value) : undefined
		}));
	};

	if (loading) {
		return (
			<Card className="border-none shadow-xs">
				<CardContent className="p-6 flex items-center justify-center">
					<Loader2 className="w-6 h-6 animate-spin text-primary" />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="border-none shadow-xs">
			<CardContent className="p-6">
				<div className="flex items-start gap-3 mb-6">
					<div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
						<CreditCard className="w-5 h-5 text-primary" />
					</div>
					<div>
						<h2 className="text-lg font-semibold text-foreground">
							{translate('admin.flexibleCompensation.title')}
						</h2>
						<p className="text-sm text-muted-foreground mt-0.5">
							{translate('admin.flexibleCompensation.description')}
						</p>
					</div>
				</div>

				<div className="space-y-4">
					{FLEX_COMP_ITEMS.map((item) => {
						const isEnabled = config[item.key] as boolean;
						const amount = config[item.amountKey] as number | undefined;

						return (
							<div
								key={item.key}
								className="rounded-lg border border-border p-4 transition-colors hover:bg-muted/30"
							>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-3">
										<div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
											{item.icon}
										</div>
										<div>
											<p className="font-medium text-foreground">{translate(item.labelKey)}</p>
											<p className="text-xs text-muted-foreground">
												{item.isAnnual
													? translate('admin.flexibleCompensation.annualAmount')
													: translate('admin.flexibleCompensation.monthlyAmount')}
											</p>
										</div>
									</div>
									<Switch
										checked={isEnabled}
										onCheckedChange={() => toggleItem(item.key, item.amountKey)}
									/>
								</div>

								{isEnabled && (
									<div className="mt-4 ml-12">
										<Label className="text-sm text-muted-foreground mb-1.5 block">
											{item.isAnnual
												? translate('admin.flexibleCompensation.annualLimit')
												: translate('admin.flexibleCompensation.monthlyLimit')}
										</Label>
										<div className="flex items-center gap-2">
											<Input
												type="number"
												min={0}
												max={999999}
												placeholder={item.placeholder}
												value={amount ?? ''}
												onChange={(e) => updateAmount(item.amountKey, e.target.value)}
												className="w-32"
											/>
											<span className="text-sm text-muted-foreground">€</span>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				<div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-border">
					{saved && (
						<div className="flex items-center gap-1.5 text-primary text-sm">
							<Check className="w-4 h-4" />
							{translate('admin.flexibleCompensation.saveSuccess')}
						</div>
					)}
					<Button
						onClick={handleSave}
						disabled={saving}
						className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full"
					>
						{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
						{translate('admin.flexibleCompensation.save')}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
};

export default AdminFlexibleCompensationConfigV2;
